import {defaultPropertyDefinitions} from "@buildwithflux/constants";
import {
    ElementHelper,
    getComputedPropertiesForNet,
    type MixedOrSingleProperty,
    type PropertySpecialState,
} from "@buildwithflux/core";
import type {
    IDocumentData,
    IElementData,
    IElementsMap,
    INetsMap,
    IPropertiesMap,
    IPropertyData,
    IRouteData,
} from "@buildwithflux/models";
import {isEqual} from "lodash";

import type {DocumentStoreState} from "../types";

/**
 * Type for the current subject selection result
 */
export interface ICurrentSubject {
    uid: string;
    subjectData?: IElementData | INetsMap[string] | IDocumentData["pcbLayoutNodes"] extends Record<string, infer T>
        ? T
        : never;
    type: "elements" | "routes" | "document" | "nodes" | "nets";
}

// ============================================================================
// Pure Functions
// ============================================================================

/**
 * Pure function to count selected elements (excluding branch points)
 * @param elements - The elements map
 * @param elementUids - Array of element UIDs to count
 * @returns The count of selected elements
 */
export function computeSelectedElementsCount(
    elements: IDocumentData["elements"] | undefined,
    elementUids: string[] | undefined,
): number {
    if (!elements || !elementUids) {
        return 0;
    }
    let count = 0;
    for (const uid of elementUids) {
        const element = elements[uid];
        if (element && !ElementHelper.isBranchPointElement(element)) {
            count++;
        }
    }
    return count;
}

/**
 * Pure function to get the current subject based on selection.
 * This determines what type of subject is currently selected (element, net, node, or document).
 * @param documentUid - The document UID
 * @param elements - The elements map
 * @param nets - The nets map
 * @param pcbLayoutNodes - The PCB layout nodes map (pass empty object if not in PCB mode)
 * @param selectedSubjectsUids - Array of selected subject UIDs
 * @returns The current subject info
 */
export function computeCurrentSubject(
    documentUid: string | undefined,
    elements: IElementsMap | undefined,
    nets: INetsMap | undefined,
    pcbLayoutNodes: IDocumentData["pcbLayoutNodes"] | undefined,
    selectedSubjectsUids: string[],
): ICurrentSubject {
    const safeElements = elements ?? {};
    const safeNets = nets ?? {};
    const safeNodes = pcbLayoutNodes ?? {};

    for (const uid of selectedSubjectsUids) {
        const element = safeElements[uid];
        if (element) {
            return {uid: element.uid, subjectData: element, type: "elements" as const};
        }
    }

    for (const uid of selectedSubjectsUids) {
        const node = safeNodes[uid];
        if (node) {
            return {uid: node.uid, subjectData: node, type: "nodes" as const};
        }
    }

    for (const uid of selectedSubjectsUids) {
        const net = safeNets[uid];
        if (net) {
            return {uid: net.uid, subjectData: net, type: "nets" as const};
        }
    }

    return {uid: documentUid || "", type: "document" as const};
}

/**
 * Pure function to merge properties from selected subjects (elements, nets, routes).
 * When multiple subjects are selected, properties are merged and marked as "mixed" if values differ.
 * Only properties that exist on ALL selected subjects are included in the result.
 *
 * @param documentProperties - The document-level properties
 * @param elements - The elements map
 * @param routes - The routes map
 * @param nets - The nets map
 * @param selectedSubjectsUids - Array of selected subject UIDs
 * @returns Merged properties map, or document properties if no subjects are selected
 */
export function mergeSelectedSubjectProperties(
    documentProperties: IPropertiesMap | undefined,
    elements: IElementsMap | undefined,
    routes: IDocumentData["routes"] | undefined,
    nets: INetsMap | undefined,
    selectedSubjectsUids: string[],
): Record<string, MixedOrSingleProperty> | IPropertiesMap | undefined {
    if (documentProperties && selectedSubjectsUids.length === 0) {
        return documentProperties;
    }

    const safeElements = elements ?? {};
    const safeRoutes = routes ?? {};
    const safeNets = nets ?? {};

    const selectedElements: IElementData[] = [];
    const selectedRoutes: IRouteData[] = [];
    const selectedNets: INetsMap[string][] = [];

    for (const uid of selectedSubjectsUids) {
        if (safeElements[uid]) {
            selectedElements.push(safeElements[uid]!);
        }
        if (safeRoutes[uid]) {
            selectedRoutes.push(safeRoutes[uid]!);
        }
        if (safeNets[uid]) {
            selectedNets.push(safeNets[uid]!);
        }
    }

    const mergedProperties: Record<string, MixedOrSingleProperty> = {};
    let firstIteration = true;

    const mergeProperties = (subject: IElementData | INetsMap[string] | IRouteData): void => {
        if (!subject.properties) return;

        const subjectPropertyNames = new Set<string>();
        for (const property of Object.values(subject.properties)) {
            if (firstIteration) {
                mergedProperties[property.name] = property;
            } else if (mergedProperties[property.name]) {
                const existingProperty = mergedProperties[property.name]!;
                const {uid: _uid, ...propertyWithoutUid} = existingProperty as IPropertyData;
                const multiProperty: PropertySpecialState = {
                    ...propertyWithoutUid,
                    state: "state" in existingProperty ? (existingProperty as PropertySpecialState).state : "multiple",
                };
                if (!isEqual(multiProperty.value, property.value)) {
                    multiProperty.value = "";
                    multiProperty.state = "mixed";
                }
                mergedProperties[property.name] = multiProperty;
            }
            subjectPropertyNames.add(property.name);
        }

        for (const key of Object.keys(mergedProperties)) {
            if (!subjectPropertyNames.has(key)) {
                delete mergedProperties[key];
            }
        }

        firstIteration = false;
    };

    for (const element of selectedElements) {
        mergeProperties(element);
    }
    for (const net of selectedNets) {
        mergeProperties(net);
    }
    for (const route of selectedRoutes) {
        mergeProperties(route);
    }

    // Enrich net properties with computed values for Current and Max Current
    // Only do this when a single net is selected (not for multi-selection)
    // Also check that the net has pins defined (required by getComputedPropertiesForNet)
    if (selectedNets.length === 1 && selectedElements.length === 0 && selectedRoutes.length === 0) {
        const net = selectedNets[0]!;
        if (!net.pins) {
            return mergedProperties;
        }
        const computedProperties = getComputedPropertiesForNet(net, safeElements);

        const currentPropertyName = defaultPropertyDefinitions.current.label;
        const currentProperty = mergedProperties[currentPropertyName];
        if (currentProperty && currentProperty.isSetBySystem && !currentProperty.value) {
            const computedCurrent = computedProperties[defaultPropertyDefinitions.current.key];
            if (computedCurrent && computedCurrent.length > 0) {
                const computedValue = computedCurrent[0]!;
                mergedProperties[currentPropertyName] = {
                    ...currentProperty,
                    value: computedValue.value,
                    computedValueSource: computedValue.source,
                };
            }
        }

        const maxCurrentPropertyName = defaultPropertyDefinitions.max_current.label;
        const maxCurrentProperty = mergedProperties[maxCurrentPropertyName];
        if (maxCurrentProperty && maxCurrentProperty.isSetBySystem && !maxCurrentProperty.value) {
            const computedMaxCurrent = computedProperties[defaultPropertyDefinitions.max_current.key];
            if (computedMaxCurrent && computedMaxCurrent.length > 0) {
                const computedValue = computedMaxCurrent[0]!;
                mergedProperties[maxCurrentPropertyName] = {
                    ...maxCurrentProperty,
                    value: computedValue.value,
                    computedValueSource: computedValue.source,
                };
            }
        }
    }

    return mergedProperties;
}

// ============================================================================
// Selectors
// ============================================================================

/**
 * Selector to get the count of selected elements (excluding branch points).
 * @param elementUids - Optional array of element UIDs to count
 */
export const selectedElementsCount = (elementUids?: string[]): ((state: DocumentStoreState) => number) => {
    return (state: DocumentStoreState): number => {
        return computeSelectedElementsCount(state.elements, elementUids);
    };
};

/**
 * Selector to get the current subject based on selection.
 * @param selectedSubjectsUids - Array of selected subject UIDs
 * @param isPCB - Whether we're in PCB mode (to check for PCB nodes)
 */
export const currentSubject = (
    selectedSubjectsUids: string[],
    isPCB: boolean,
): ((state: DocumentStoreState) => ICurrentSubject) => {
    return (state: DocumentStoreState): ICurrentSubject => {
        const pcbLayoutNodes = isPCB ? state.pcbLayoutNodes : undefined;
        return computeCurrentSubject(state.uid, state.elements, state.nets, pcbLayoutNodes, selectedSubjectsUids);
    };
};

/**
 * Selector to get the merged properties of all selected subjects.
 * @param selectedSubjectsUids - Array of selected subject UIDs
 */
export const selectedSubjectProperties = (
    selectedSubjectsUids: string[],
): ((state: DocumentStoreState) => Record<string, MixedOrSingleProperty> | IPropertiesMap | undefined) => {
    return (state: DocumentStoreState): Record<string, MixedOrSingleProperty> | IPropertiesMap | undefined => {
        return mergeSelectedSubjectProperties(
            state.properties,
            state.elements,
            state.routes,
            state.nets,
            selectedSubjectsUids,
        );
    };
};
